/**
 * VideoActions Component
 * Action buttons for video content (similar to ImageActions)
 * 
 * Features:
 * - Send to device
 * - Duplicate video
 * - Trim first/last frames
 * - Delete video
 */

import React from 'react';
import { Box, IconButton, Tooltip } from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import ContentCutIcon from '@mui/icons-material/ContentCut';
import DownloadIcon from '@mui/icons-material/Download';
import PhoneIphoneIcon from '@mui/icons-material/PhoneIphone';
import CopyAllIcon from '@mui/icons-material/CopyAll';

interface VideoActionsProps {
  onTrim?: () => void;
  onDelete?: () => void;
  onSendToDevice?: () => void;
  onDownload?: () => void;
  onDuplicate?: () => void;
  show: boolean;
  showDelete?: boolean;
  showTrim?: boolean;
  showSendToDevice?: boolean;
  showDownload?: boolean;
  showDuplicate?: boolean;
}

export const VideoActions: React.FC<VideoActionsProps> = ({
  onTrim,
  onDelete,
  onSendToDevice,
  onDownload,
  onDuplicate,
  show,
  showDelete = true,
  showTrim = true,
  showSendToDevice = false,
  showDownload = false,
  showDuplicate = false,
}) => {
  if (!show) return null;

  return (
    <Box
      sx={{
        position: 'absolute',
        top: 12,
        right: 12,
        display: 'flex',
        gap: '6px',
        opacity: 0,
        transition: 'opacity 0.2s ease',
        zIndex: 10,
        // Also used by the audio arm of PagedOutput — same rail, same actions.
        // Mirrors the multi-selector precedent in ImageActions.tsx.
        '.video-container:hover &, .audio-container:hover &': {
          opacity: 1,
        },
      }}
    >
      {showSendToDevice && onSendToDevice && (
        <Tooltip title="Send to Device" placement="top">
          <IconButton
            onClick={(e) => {
              e.stopPropagation();
              onSendToDevice();
            }}
            size="small"
            sx={{
              backgroundColor: 'rgba(0, 0, 0, 0.5)',
              border: '1px solid rgba(255, 255, 255, 0.2)',
              color: 'white',
              '&:hover': {
                backgroundColor: 'rgba(59, 130, 246, 0.7)',
                borderColor: 'rgba(59, 130, 246, 0.5)',
              },
              transition: 'all 0.2s ease',
              padding: '8px',
            }}
          >
            <PhoneIphoneIcon sx={{ fontSize: '18px' }} />
          </IconButton>
        </Tooltip>
      )}

      {showDownload && onDownload && (
        <Tooltip title="Download" placement="top">
          <IconButton
            onClick={(e) => {
              e.stopPropagation();
              onDownload();
            }}
            size="small"
            sx={{
              backgroundColor: 'rgba(0, 0, 0, 0.5)',
              border: '1px solid rgba(255, 255, 255, 0.2)',
              color: 'white',
              '&:hover': {
                backgroundColor: 'rgba(59, 130, 246, 0.7)',
                borderColor: 'rgba(59, 130, 246, 0.5)',
              },
              transition: 'all 0.2s ease',
              padding: '8px',
            }}
          >
            <DownloadIcon sx={{ fontSize: '18px' }} />
          </IconButton>
        </Tooltip>
      )}

      {showDuplicate && onDuplicate && (
        <Tooltip title="Duplicate" placement="top">
          <IconButton
            onClick={(e) => {
              e.stopPropagation();
              onDuplicate();
            }}
            size="small"
            sx={{
              backgroundColor: 'rgba(0, 0, 0, 0.5)',
              border: '1px solid rgba(255, 255, 255, 0.2)',
              color: 'white',
              '&:hover': {
                backgroundColor: 'rgba(124, 58, 237, 0.7)',
                borderColor: 'rgba(124, 58, 237, 0.5)',
              },
              transition: 'all 0.2s ease',
              padding: '8px',
            }}
          >
            <CopyAllIcon sx={{ fontSize: '18px' }} />
          </IconButton>
        </Tooltip>
      )}

      {showTrim && onTrim && (
        <Tooltip title="Trim video" placement="top">
          <IconButton
            onClick={(e) => {
              e.stopPropagation();
              onTrim();
            }}
            size="small"
            sx={{
              backgroundColor: 'rgba(0, 0, 0, 0.5)',
              border: '1px solid rgba(255, 255, 255, 0.2)',
              color: 'white',
              '&:hover': {
                backgroundColor: 'rgba(100, 180, 255, 0.7)',
                borderColor: 'rgba(100, 180, 255, 0.5)',
              },
              transition: 'all 0.2s ease',
              padding: '8px',
            }}
          >
            <ContentCutIcon sx={{ fontSize: '18px' }} />
          </IconButton>
        </Tooltip>
      )}
      
      {showDelete && onDelete && (
        <Tooltip title="Delete" placement="top">
          <IconButton
            onClick={(e) => {
              e.stopPropagation();
              onDelete();
            }}
            size="small"
            sx={{
              backgroundColor: 'rgba(220, 38, 38, 0.6)',
              border: '1px solid rgba(255, 255, 255, 0.2)',
              color: 'white',
              '&:hover': {
                backgroundColor: 'rgba(220, 38, 38, 0.9)',
                borderColor: 'rgba(220, 38, 38, 0.7)',
              },
              transition: 'all 0.2s ease',
              padding: '8px',
            }}
          >
            <DeleteIcon sx={{ fontSize: '18px' }} />
          </IconButton>
        </Tooltip>
      )}
    </Box>
  );
};

export default VideoActions;
